import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetcher } from '@remix-run/react';
import type { Pagination, PostData } from '@shared/dream-components';

import type { PostCard } from '~/interfaces/post';

import buildPostSearchParams from './buildPostSearchParams';

export const calculateBackendPage = (displayPage: number, pagesSkipped: number): number => {
  if (!displayPage || displayPage <= 0) return 1;
  if (!pagesSkipped || pagesSkipped <= 0) return displayPage;
  const result = displayPage + pagesSkipped;
  return Number.isNaN(result) ? displayPage : result;
};

interface Arguments {
  defaultPosts: PostData[];
  pagination: Pagination | null;
  shouldSendPaginationParams?: boolean;
  enabled?: boolean;
  postGroupType: string;
  hasFakeData: boolean;
  tags: string[];
  categoryTagsMatch?: 'any' | 'all';
  onPageFetchSuccess?: (newPostIds: string[]) => void;
  pagesSkippedByOffset?: number;
  audience?: string;
  blockId?: string;
  authorIds?: string[];
}

export const transformToPostData = (posts: PostCard[]) =>
  posts.map(
    (post: PostCard) =>
      ({
        id: post.id,
        web_title: post.web_title,
        web_subtitle: post.web_subtitle ?? undefined,
        comments_state: post.comments_state ?? '',
        override_scheduled_at: post.override_scheduled_at,
        audience: post.audience,
        slug: post.slug,
        content_tags: post.content_tags,
        // PostData types this as a string, but a post with no thumbnail anywhere sends null.
        image_url: post.image_url as PostData['image_url'],
        authors: post.authors,
        is_premium: post?.is_premium,
        estimated_reading_time: post?.estimated_reading_time,
        estimated_reading_time_display: post?.estimated_reading_time_display,
      }) as PostData,
  );

const usePaginatedPosts = ({
  defaultPosts = [],
  pagination,
  shouldSendPaginationParams = true,
  enabled = true,
  postGroupType,
  hasFakeData,
  tags = [],
  categoryTagsMatch = 'any',
  onPageFetchSuccess,
  pagesSkippedByOffset,
  audience,
  blockId,
  authorIds,
}: Arguments) => {
  // Unique fetcher key prevents state collision when multiple post blocks exist on the same page
  const fetcher = useFetcher<any>({ key: blockId ? `posts-${blockId}` : undefined });
  const fetchedData = fetcher?.data;
  const fetchedPagination = fetchedData?.pagination;

  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [paginationOperation, setPaginationOperation] = useState<'load_more' | 'go_to_page' | null>(null);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [contentTagsMatch, setContentTagsMatch] = useState<'any' | 'all'>('any');

  const [posts, setPosts] = useState<PostData[]>(defaultPosts);
  const [currentPage, setCurrentPage] = useState((fetchedPagination ? fetchedPagination.page : pagination?.page) || 0);
  const [totalPages, setTotalPages] = useState(
    (fetchedPagination ? fetchedPagination.total_pages : pagination?.total_pages) || 0,
  );
  const [perPage, setPerPage] = useState(fetchedPagination ? fetchedPagination.per_page : pagination?.per_page);
  const configuredPerPage = useRef(pagination?.per_page);

  const handleTagFilterChange = useCallback(
    (newTags: string[]) => {
      setSelectedTagFilters(newTags);
      // Reset match mode to OR when fewer than 2 tags selected
      const effectiveMatch = newTags.length < 2 ? 'any' : contentTagsMatch;
      setContentTagsMatch(effectiveMatch);
      // Reset to first page and fetch with new filters
      setCurrentPage(0);
      setPaginationOperation('go_to_page');

      const searchParams = buildPostSearchParams({
        query,
        page: 1,
        perPage,
        selectedTagFilters: newTags,
        categoryTags: tags,
        categoryTagsMatch,
        contentTagsMatch: effectiveMatch,
        audience,
        authorIds,
      });

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [fetcher, perPage, query, tags, categoryTagsMatch, contentTagsMatch, audience, authorIds],
  );

  const handleContentTagsMatchChange = useCallback(
    (newMatch: 'any' | 'all') => {
      setContentTagsMatch(newMatch);
      setCurrentPage(0);
      setPaginationOperation('go_to_page');

      const searchParams = buildPostSearchParams({
        query,
        page: 1,
        perPage,
        selectedTagFilters,
        categoryTags: tags,
        categoryTagsMatch,
        contentTagsMatch: newMatch,
        audience,
        authorIds,
      });

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [fetcher, perPage, query, tags, categoryTagsMatch, selectedTagFilters, audience, authorIds],
  );

  const fetchPosts = useCallback(
    (searchQuery?: string, options?: { page?: number; perPage?: number }) => {
      if (!enabled) {
        return;
      }

      const effectivePage = options?.page ?? currentPage;
      const effectivePerPage = options?.perPage ?? perPage ?? configuredPerPage.current;
      const isPaginationEnabled = shouldSendPaginationParams && effectivePage > 0;

      const searchParams = buildPostSearchParams({
        query: searchQuery,
        page: isPaginationEnabled ? effectivePage : 0,
        perPage: shouldSendPaginationParams ? effectivePerPage : undefined,
        selectedTagFilters,
        categoryTags: tags,
        categoryTagsMatch,
        contentTagsMatch,
        audience,
        authorIds,
      });

      const url = `/posts?${searchParams.toString()}`;
      fetcher.load(url);
    },
    [
      fetcher,
      currentPage,
      perPage,
      shouldSendPaginationParams,
      enabled,
      selectedTagFilters,
      tags,
      contentTagsMatch,
      audience,
      authorIds,
    ],
  );

  const fetchNextPage = useCallback(
    (searchQuery?: string) => {
      if (!enabled) {
        return;
      }

      setPaginationOperation('load_more');

      const searchParams = buildPostSearchParams({
        query: searchQuery,
        page: currentPage + 1,
        perPage,
        selectedTagFilters,
        categoryTags: tags,
        categoryTagsMatch,
        contentTagsMatch,
        audience,
        authorIds,
      });

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [
      currentPage,
      fetcher,
      perPage,
      enabled,
      selectedTagFilters,
      tags,
      categoryTagsMatch,
      contentTagsMatch,
      audience,
      authorIds,
    ],
  );

  const goToPage = useCallback(
    (page: number, searchQuery?: string) => {
      if (!enabled) return;

      setPaginationOperation('go_to_page');

      const backendPage = calculateBackendPage(page, pagesSkippedByOffset || 0);

      const searchParams = buildPostSearchParams({
        query: searchQuery,
        page: backendPage,
        perPage,
        selectedTagFilters,
        categoryTags: tags,
        categoryTagsMatch,
        contentTagsMatch,
        audience,
        authorIds,
      });

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [
      fetcher,
      perPage,
      enabled,
      selectedTagFilters,
      tags,
      categoryTagsMatch,
      pagesSkippedByOffset,
      contentTagsMatch,
      audience,
      authorIds,
    ],
  );

  const resetSearchState = useCallback(() => {
    setIsSearching(false);
    setCurrentPage(pagination?.page || 0);
    setTotalPages(pagination?.total_pages || 0);
    setPerPage(pagination?.per_page || 0);
    setPosts(defaultPosts);
    setSelectedTagFilters([]);
    setContentTagsMatch('any');
  }, [pagination, defaultPosts]);

  const handleDebouncedSearchQuery = useCallback(
    (searchQuery: string) => {
      setTimeout(() => {
        setQuery(searchQuery);
      }, 100);
    },
    [setQuery],
  );

  // This is to optimally render the search loading state when a user starts typing to give the impression its searching
  // The debounced state is whats really triggering the query so we dont send too many requests
  const handleNonDebouncedSearchQuery = useCallback(
    (_searchQuery: string) => {
      setIsSearching(true);
      setCurrentPage(0);
      setTotalPages(0);
      setPosts([]);
    },
    [setIsSearching, setCurrentPage, setTotalPages, setPosts],
  );

  // Reset pagination and posts in state
  // when search query changes
  // and fetch posts
  useEffect(() => {
    // This condition avoids resetting the posts on mount
    // when the data is fake or post group type is any other type
    // but "archive" or "tag_page" or "author_page"
    const isNotTagPage = postGroupType !== 'tag_page';
    const isNotAuthorPage = postGroupType !== 'author_page';
    const isNotArchive = postGroupType !== 'archive';
    const isIncorrectPostGroupType = isNotTagPage && isNotAuthorPage && isNotArchive;

    if (hasFakeData || isIncorrectPostGroupType) {
      return;
    }

    if (typeof query === 'string' && query.trim()) {
      fetchPosts(query);
    }

    if (typeof query === 'string' && !query.trim()) {
      resetSearchState();
    }
  }, [hasFakeData, query]);

  // Update state when data is fetched
  // Note: Only fetchedData in deps to avoid race condition where effect runs
  // when paginationOperation changes but before new data arrives
  useEffect(() => {
    if (!fetchedData) return;

    const fetchedPage = fetchedData?.pagination?.page;
    const newPosts = transformToPostData(fetchedData?.posts || []);

    if (paginationOperation === 'go_to_page') {
      // Replace posts for traditional pagination
      setPosts(newPosts);
      setCurrentPage(fetchedPage || 1);
      setPerPage(fetchedData?.pagination?.per_page);
      setTotalPages(fetchedData?.pagination?.total_pages);
      setIsSearching(false);
      setPaginationOperation(null);
      onPageFetchSuccess?.((fetchedData?.posts || []).map((post: PostCard) => post.id));
    } else if (paginationOperation === 'load_more' || currentPage === 0) {
      // Append posts for load more functionality
      setPosts(posts.concat(newPosts));
      setCurrentPage(fetchedPage || currentPage + 1);
      setPerPage(fetchedData?.pagination?.per_page);
      setTotalPages(fetchedData?.pagination?.total_pages);
      setIsSearching(false);
      setPaginationOperation(null);
      onPageFetchSuccess?.((fetchedData?.posts || []).map((post: PostCard) => post.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedData]);

  return {
    posts,
    showLoadMore: currentPage + 1 <= totalPages,
    isLoading: fetcher?.state !== 'idle',
    postsFetcher: fetcher,
    fetchPosts,
    loadMorePosts: fetchNextPage,
    goToPage,
    currentPage,
    setCurrentPage,
    setTotalPages,
    setPerPage,
    setPosts,
    isSearching,
    setIsSearching,
    resetSearchState,
    handleDebouncedSearchQuery,
    handleNonDebouncedSearchQuery,
    searchQuery: query,
    setSearchQuery: setQuery,
    selectedTagFilters,
    handleTagFilterChange,
    contentTagsMatch,
    handleContentTagsMatchChange,
  };
};

export default usePaginatedPosts;
